import usePackageSummary from '@/catalog/hooks/use-package-summary';
import usePDPInsurancePlanSubmit from '@/catalog/hooks/use-pdp-insurance-plan-submit';
import usePDPSelectPlanSubmit from '@/catalog/hooks/use-pdp-select-plan-submit';
import { VehiclePlan, InsurancePlan, InsurancePlanTier } from '@/catalog/types';
import Spacer from '@/common/components/spacer';
import { Form } from 'react-router';
import ProceedToSchedule from '../proceed-to-schedule/proceed-to-schedule';
import InsurancePlanSelection from './insurance-plan-selection';
import MileagePlanSelection, { CreditConfig } from './mileage-plan-selection';
import PlanSelectionPanelContainer from './plan-selection-panel-container';
import { PDPErrors } from '@/catalog/types/pdp-action';
import PDPMembershipCard from '../pdp-membership-card';

export type PlanSelectionErrorsConfig = {
  errors?: PDPErrors;
  dismissedPlanGuide: boolean;
};

export type PlanSelectionRefs = {
  mileagePlanSectionRef: React.RefObject<HTMLDivElement | null>;
  insurancePlanSectionRef: React.RefObject<HTMLDivElement | null>;
};

interface CheckoutPlanSelectionPanelProps {
  // Mileage plans
  vehiclePlans: VehiclePlan[];
  selectedMileagePlan?: number;
  mostPopularPlanMileageAmount?: string;
  splitPayment?: boolean;
  fullstoryPlanEventDelegate?: () => void;

  // Insurance plans
  insurancePlans?: InsurancePlan[];
  selectedInsuranceTier?: InsurancePlanTier;
  enableInsuranceChange?: boolean;
  fullstoryInsuranceEventDelegate?: () => void;

  // Membership
  gasSaverCents: string;
  MEMBERSHIP_COST: string;
  vehicleImagesColumnRef?: React.RefObject<HTMLDivElement | null>;

  // Savings
  savingsAvailable?: boolean;
  estimatedSavings?: number;
  handleSavingsClick?: () => void;
  fullstoryOpenPlanPanelSavingsEventDelegate?: () => void;
  fullstoryOpenMobileStickyFooterSavingsEventDelegate?: () => void;

  // Additional config
  errorsConfig?: PlanSelectionErrorsConfig;
  isOrderable: boolean;
  price_savings_1200_over_850?: number;
  price_per_mile_850_plan_for_1200_miles?: number;
  price_difference_850_vs_1200?: number;
  creditConfig: CreditConfig;
  fullstoryContinueEventDelegate?: () => void;
  isSubmittingContinue?: boolean;
  continueButtonRef?: (node?: Element | null) => void;
  planSelectionRefs: PlanSelectionRefs;
  PUBLIC_CDN_URL: string;
}

export default function CheckoutPlanSelectionPanel({
  // Mileage plans
  vehiclePlans,
  selectedMileagePlan,
  mostPopularPlanMileageAmount,
  splitPayment = false,
  fullstoryPlanEventDelegate,

  // Insurance plans
  insurancePlans,
  selectedInsuranceTier,
  enableInsuranceChange,
  fullstoryInsuranceEventDelegate,

  // Membership
  gasSaverCents,
  MEMBERSHIP_COST,
  vehicleImagesColumnRef,

  // Savings
  savingsAvailable,
  estimatedSavings,
  handleSavingsClick,
  fullstoryOpenPlanPanelSavingsEventDelegate,
  fullstoryOpenMobileStickyFooterSavingsEventDelegate,

  // Additional config
  errorsConfig,
  isOrderable,
  price_savings_1200_over_850,
  price_per_mile_850_plan_for_1200_miles,
  price_difference_850_vs_1200,
  creditConfig,
  fullstoryContinueEventDelegate,
  continueButtonRef,
  planSelectionRefs,
  PUBLIC_CDN_URL,
}: CheckoutPlanSelectionPanelProps) {
  const hasNoSelectedAgeCreditRange =
    creditConfig.selectedCreditScoreId === undefined ||
    creditConfig.selectedIsOver25 === undefined ||
    creditConfig.selectedAgeRangeId === undefined;

  const {
    handleSelectPlan: handleSelectMilagePlan,
    selectedPlanId: selectedMileagePlanId,
  } = usePDPSelectPlanSubmit(
    vehiclePlans,
    selectedMileagePlan,
    fullstoryPlanEventDelegate
  );

  const { handleSelectInsurancePlan, selectedInsurancePlanId } =
    usePDPInsurancePlanSubmit(
      insurancePlans,
      selectedInsuranceTier,
      fullstoryInsuranceEventDelegate
    );

  const packageSummary = usePackageSummary({
    selectedMileagePlanId,
    selectedInsurancePlanId,
    vehiclePlans,
    insurancePlans,
    savingsAvailable,
    estimatedSavings,
  });

  return (
    <>
      <PlanSelectionPanelContainer dataTestId="checkout-panel" className="pb-[24px]">
        <Form method="post" className="flex flex-col px-[24px]">
          <MileagePlanSelection
            // Mileage plans
            vehiclePlans={vehiclePlans}
            selectedMileagePlanId={selectedMileagePlanId}
            mostPopularPlanMileageAmount={mostPopularPlanMileageAmount}
            splitPayment={splitPayment}
            handleSelectMilagePlan={handleSelectMilagePlan}
            // Additional config
            errorConfig={{
              error: errorsConfig?.errors?.plan,
              dismissedPlanGuide: errorsConfig?.dismissedPlanGuide ?? true,
            }}
            inSwapFlow={false}
            isOrderable={isOrderable}
            price_savings_1200_over_850={price_savings_1200_over_850}
            price_per_mile_850_plan_for_1200_miles={
              price_per_mile_850_plan_for_1200_miles
            }
            price_difference_850_vs_1200={price_difference_850_vs_1200}
            creditConfig={creditConfig}
            popoverTriggerRef={planSelectionRefs.mileagePlanSectionRef}
            hasNoSelectedAgeCreditRange={hasNoSelectedAgeCreditRange}
          />

          <Spacer variant="height32" />

          <InsurancePlanSelection
            // Insurance plans
            insurancePlans={insurancePlans}
            selectedInsurancePlanId={selectedInsurancePlanId}
            enableInsuranceChange={enableInsuranceChange}
            handleSelectInsurancePlan={handleSelectInsurancePlan}
            // Additional config
            errorConfig={{
              error: errorsConfig?.errors?.insurancePlan,
              dismissedPlanGuide: errorsConfig?.dismissedPlanGuide ?? true,
            }}
            selectedMileagePlan={selectedMileagePlan}
            isOrderable={isOrderable}
            inSwapFlow={false}
            popoverTriggerRef={planSelectionRefs.insurancePlanSectionRef}
            creditConfig={creditConfig}
            hasNoSelectedAgeCreditRange={hasNoSelectedAgeCreditRange}
          />

          <Spacer variant="height40" hideForBreakpoints={['sm', 'md']} />

          <ProceedToSchedule
            isOrderable={isOrderable}
            packageSummary={packageSummary}
            hasNoSelectedAgeCreditRange={hasNoSelectedAgeCreditRange}
            fullstoryContinueEventDelegate={fullstoryContinueEventDelegate}
            fullstoryOpenPlanPanelSavingsEventDelegate={
              fullstoryOpenPlanPanelSavingsEventDelegate
            }
            fullstoryOpenMobileStickyFooterSavingsEventDelegate={
              fullstoryOpenMobileStickyFooterSavingsEventDelegate
            }
            continueButtonRef={continueButtonRef}
            handleSavingsClick={handleSavingsClick}
          />
        </Form>
      </PlanSelectionPanelContainer>

      <Spacer variant="height24" />

      <PDPMembershipCard
        membershipCost={MEMBERSHIP_COST}
        gasSaverCents={gasSaverCents}
        PUBLIC_CDN_URL={PUBLIC_CDN_URL}
        vehicleImagesColumnRef={vehicleImagesColumnRef}
      />
    </>
  );
}
